/**
 * Tasks command - list and manage remote tasks
 */

import chalk from 'chalk';
import { loadTasks, updateTask, getTask, pruneTasks, resolveAuthToken, sandboxClientOptions } from '../config.js';
import { SANDBOX_WORKER_URL } from '@spacecode/shared/remote';
import type { TaskStatus, RemoteTask } from '@spacecode/shared/remote';
import { truncate } from '../utils/task.js';

export interface TasksOptions {
  all?: boolean;
  prune?: boolean;
  status?: string;
  limit?: number;
  repo?: string;
  cleanStale?: boolean;
  staleAge?: number;  // Hours to consider a task stale (default: 12)
  forceClean?: boolean;  // Mark as stale even if sandbox is still running
}

/**
 * Color-coded status string
 */
function formatStatus(status: TaskStatus): string {
  switch (status) {
    case 'pending':
      return chalk.yellow(status);
    case 'running':
      return chalk.cyan(status);
    case 'completed':
      return chalk.green(status);
    case 'failed':
      return chalk.red(status);
    case 'cancelled':
      return chalk.gray(status);
    default:
      return status;
  }
}

/**
 * Human-readable age from ISO date string
 */
function formatAge(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffSecs = Math.floor((now - then) / 1000);

  if (diffSecs < 60) {
    return `${diffSecs}s ago`;
  }
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

/**
 * Strip github.com/ prefix from repo string
 */
function shortRepo(repo: string): string {
  return repo.replace(/^(https?:\/\/)?github\.com\//, '');
}

/**
 * Display a single task
 */
function displayTask(task: RemoteTask): void {
  const id = chalk.bold(task.id);
  const status = formatStatus(task.status);
  const age = chalk.dim(formatAge(task.createdAt));

  // Line 1: ID, status, age
  console.log(`  ${id}  ${status}  ${age}`);

  // Line 2: Repo and mode
  console.log(`    ${shortRepo(task.repo)}  ${chalk.dim(`(${task.mode})`)}`);

  // Line 3: Prompt (truncated)
  console.log(`    ${chalk.dim(truncate(task.prompt, 50))}`);

  // Optional: PR URL
  if (task.prUrl) {
    console.log(`    ${chalk.cyan(task.prUrl)}`);
  }

  // Optional: Branch
  if (task.branch) {
    console.log(`    branch: ${chalk.dim(task.branch)}`);
  }

  // Optional: Error
  if (task.error) {
    console.log(`    ${chalk.red(`error: ${task.error}`)}`);
  }
}

/**
 * List and manage remote tasks
 */
export async function tasks(options: TasksOptions = {}): Promise<void> {
  // Handle --prune flag
  if (options.prune) {
    const pruned = await pruneTasks();
    console.log(`Pruned ${pruned} old task(s).`);
    return;
  }

  // Handle --clean-stale flag
  if (options.cleanStale) {
    const staleAgeHours = options.staleAge || 12;
    const staleThresholdMs = staleAgeHours * 60 * 60 * 1000;
    const now = Date.now();

    const allTasks = await loadTasks();
    let cleaned = 0;

    for (const task of allTasks) {
      // Only clean running tasks that are older than the threshold
      if (task.status === 'running') {
        const taskAge = now - new Date(task.createdAt).getTime();
        if (taskAge > staleThresholdMs) {
          // Sandbox tasks complete synchronously (exec blocks until done),
          // so any "running" task older than the threshold is stale.
          await updateTask(task.id, {
            status: 'completed',
            completedAt: new Date().toISOString(),
          });
          cleaned++;
        }
      }
    }

    if (cleaned > 0) {
      console.log(`Marked ${cleaned} stale task(s) (older than ${staleAgeHours}h) as completed.`);
    } else {
      console.log(`No stale tasks found (older than ${staleAgeHours}h).`);
    }
    return;
  }

  let allTasks = await loadTasks();

  if (allTasks.length === 0) {
    console.log(chalk.dim('No tasks found.'));
    console.log(chalk.dim('\nRun `spacecode exec <repo> "<prompt>"` to create one.'));
    return;
  }

  // For running tasks, check if they should be marked completed.
  // Sandbox tasks complete synchronously, so "running" in local records
  // means the task was interrupted or the record is stale.
  // We can optionally probe the sandbox worker, but for simplicity
  // just leave the status as-is and let --clean-stale handle it.

  // Apply filters
  let filteredTasks = allTasks;

  // Filter by status
  if (options.status) {
    filteredTasks = filteredTasks.filter(t => t.status === options.status);
  }

  // Filter by repo
  if (options.repo) {
    filteredTasks = filteredTasks.filter(t => t.repo.includes(options.repo!));
  }

  // Filter to active tasks unless --all
  if (!options.all && !options.status) {
    filteredTasks = filteredTasks.filter(t => t.status === 'running' || t.status === 'pending');
  }

  // Apply limit
  if (options.limit && options.limit > 0) {
    filteredTasks = filteredTasks.slice(0, options.limit);
  }

  if (filteredTasks.length === 0) {
    console.log(chalk.dim('No matching tasks.'));
    if (!options.all && !options.status) {
      console.log(chalk.dim('Use --all to show completed/cancelled tasks.'));
    }
    return;
  }

  console.log(chalk.bold('\nTasks\n'));

  for (const task of filteredTasks) {
    displayTask(task);
    console.log();
  }

  // Summary
  const active = allTasks.filter(t => t.status === 'running' || t.status === 'pending').length;
  console.log(chalk.dim('─'.repeat(40)));
  console.log(chalk.dim(`${active} active / ${allTasks.length} total tasks${filteredTasks.length < allTasks.length ? ` (${filteredTasks.length} shown)` : ''}`));
}

/**
 * Cancel a running or pending task
 */
export async function cancelTask(taskId: string): Promise<void> {
  const task = await getTask(taskId);

  if (!task) {
    throw new Error(`Task ${taskId} not found.`);
  }

  if (task.status !== 'running' && task.status !== 'pending') {
    console.log(`Task ${taskId} is already ${task.status}.`);
    return;
  }

  // Destroy the associated sandbox if one exists
  if (task.machineId && task.machineId !== 'sandbox') {
    try {
      console.log(chalk.dim(`Destroying sandbox ${task.machineId}...`));
      const token = await resolveAuthToken();
      const { createSandboxClient } = await import('@spacecode/sandbox-client');
      const client = createSandboxClient(SANDBOX_WORKER_URL, token, sandboxClientOptions(token));
      await client.destroySandbox(task.machineId);
    } catch (error) {
      // Non-critical: sandbox may already be gone
      console.log(chalk.dim(`  (sandbox may already be destroyed: ${error instanceof Error ? error.message : String(error)})`));
    }
  }

  await updateTask(taskId, {
    status: 'cancelled',
    completedAt: new Date().toISOString(),
  });

  console.log(`Task ${chalk.bold(taskId)} cancelled.`);
}
